import { closeHint } from "../press-word.ts";
/**
 * The weekly scroll, drawn as a scroll (owner, 2026-10-05: "Rather than having a scroll icon should
 * look like a scroll."): a dark wooden roller across the top, the parchment with the week's lines
 * written on it in ink, a roller across the bottom, the rollers' knobs standing past the paper on
 * both sides (scripts/make-scroll.mjs, shared/weekly.ts SCROLL_ROLLER). It is its own object, no
 * frame; it takes the full width it is given, so it sits the same on the board's stone and on the
 * notice board's cork. On the board nothing moves. The app draws it alike (client/components/WeeklyScroll.tsx).
 *
 * What is written on it is the household's (owner, 2026-10-05: "Rather than the this week scroll be
 * auto generated, starting Friday evening until Saturday night, everyone should be able to write
 * down appreciations and accomplishments that will show up on the this week scroll Sunday at 5am"):
 * "This week" centred in the screen title's treatment with its divider; then "Appreciations" and
 * "Accomplishments" as small quiet labels, each line the words alone in the note slip's size (owner,
 * 2026-10-05: "The notes should be anonymous, not show who wrote them too"); a week nobody wrote on is
 * one quiet centred line. Under the title, when the scroll has one, the week's tally: four figures
 * across the paper, each over its small label, the household's totals and nobody's name (owner,
 * 2026-10-06: "The this week scroll should summarize how much gold was earned, how many tasks
 * completed, how many chores completed, how many big tasks"). While the window is open, the board's scroll in progress carries a small
 * ghost "Add yours" at the foot of the paper, bottom right (in its place, once this person's five
 * lines are written, a quiet line saying so), and a small ink pencil on the lines this person may
 * change (src/components/WeeklyWriting.tsx; owner, 2026-10-05: "Max of 5 scroll notes per week, and
 * notes cannot be removed only edited, to prevent abuse"). Owner, 2026-10-05, on the earlier hierarchy (tiny
 * headings, huge lines, shouting REMOVE and ADD YOURS): "This looks really bad".
 *
 * WeeklySheet: the scroll comes up once on the next open each week, before What's new (owner,
 * 2026-10-05: "The weekly scroll should come up on next view of the app each week, similar to the
 * what's new, and it should take priority. After seen it is on the notice board."): the scroll large
 * over the dim, "Tap to close" above it and Got it under it, where What's new has them
 * (src/weekly-sheet.ts decides when). A long scroll's writing scrolls inside the paper. In the sheet
 * it starts closed and rolls open (owner, 2026-10-05: "It should also start closed, and roll open in
 * an animation"): the two rollers together while the sheet rises, then the bottom roller travels
 * down in 8 hard frames over 400ms (--unroll-time, --unroll-steps) as the paper comes out behind it,
 * the writing already on it. The open scroll is laid out from the start, so nothing else in the
 * sheet moves: the paper is clipped and the bottom roller lifted by the paper's measured height less
 * the two tucks (--unroll-from). Reduced motion: open at once.
 */
import "./weekly-scroll.css";
import { useLayoutEffect, useRef } from "preact/hooks";
import type { Ref } from "preact";
import { useScreenCue } from "./ScreenCue";
import { useExit } from "./useExit";
import { useSheetLayer } from "../devsheet-hooks";
import { Divider } from "./PixelArt";
import { MentionText } from "./MentionText";
import { useProjectsUI } from "../features";
import {
  SCROLL_INK,
  SCROLL_ROLLER,
  SCROLL_SCALE,
  WEEKLY_ADD,
  WEEKLY_CAP_REACHED,
  WEEKLY_CARD_TITLE,
  WEEKLY_EMPTY,
  scrollLines,
  scrollSummary,
  pencilPixels,
  weeklyLineText,
  weeklySections,
  weeklyTally,
  type WeeklyLine,
  type WeeklyScroll as WeeklyScrollData,
  type WeeklySummary,
} from "../../shared/weekly.ts";

/** One roller: the strip's caps at its ends, its middle stretched between them. Decoration only. */
function Roller() {
  return <div class="weekly-roller" aria-hidden="true" />;
}

/** The paper under both rollers when closed: a tuck under each (SCROLL_ROLLER.tuck art pixels at 2x). */
const CLOSED_PAPER = SCROLL_ROLLER.tuck * SCROLL_SCALE * 2;

/**
 * Sets --unroll-from on the scroll: how far the paper is clipped and the bottom roller lifted when
 * closed (the paper's height less the two tucks), kept current if the writing reflows (a font
 * landing). Before it is set the CSS falls back to open.
 */
function useUnrollFrom(on: boolean) {
  const scrollRef = useRef<HTMLElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    const paper = paperRef.current;
    if (!on || !scroll || !paper) return;
    const set = () => scroll.style.setProperty("--unroll-from", `${Math.max(0, paper.getBoundingClientRect().height - CLOSED_PAPER)}px`);
    set();
    if (typeof ResizeObserver !== "function") return;
    const ro = new ResizeObserver(set);
    ro.observe(paper);
    return () => ro.disconnect();
  }, [on]);
  return { scrollRef, paperRef };
}

/** A line on the board's scroll in progress: the entry's id, so it can be changed. */
export type ScrollEntry = WeeklyLine & { id?: string };

/**
 * "Add yours" at the foot of the paper while the window is open: what it opens and where the focus
 * comes back. `full`: this person's lines are all written, so the quiet line shows in its place.
 */
export interface ScrollAdd {
  onAdd: () => void;
  expanded: boolean;
  addRef: Ref<HTMLButtonElement>;
  full?: boolean;
}

/** The pencil's pixels as one path, an art pixel a unit (shared/weekly.ts SCROLL_PENCIL). */
const PENCIL_PATH = pencilPixels()
  .map(([x, y]) => `M${x} ${y}h1v1h-1z`)
  .join("");

/** The small ink pencil: 12 art pixels at 24, in the paper's lighter ink, hard edges. Decoration; the button names it. */
function Pencil() {
  return (
    <svg class="weekly-pencil" width="24" height="24" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
      <path d={PENCIL_PATH} fill={SCROLL_INK.quiet} />
    </svg>
  );
}

/**
 * The week's tally under the title: four cells across the paper, each a figure over its small label
 * (Gold, Tasks, Chores, Big jobs), ink on paper. The household's totals; nobody's name.
 */
function Tally({ summary }: { summary: WeeklySummary }) {
  // Projects (docs/projects.md): with projectsUI on the big jobs' cell reads "Projects".
  const projects = useProjectsUI();
  return (
    <dl class="weekly-tally">
      {weeklyTally(summary, projects).map((c) => (
        <div class="weekly-tally-cell" key={c.key}>
          <dt>{c.label}</dt>
          <dd>{c.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * "This week" on paper: the title, the week's tally when `summary` is given, then each kind's
 * heading and its lines in the order written, or the empty week's one line. Not a button. `large` is the sheet's size; `idPrefix` keeps the
 * heading's id apart when the sheet and the board both draw one; `unroll` (the sheet's) starts it
 * closed and rolls it open once the sheet has risen. `add` and `mayEdit` are the board's while the
 * window is open: "Add yours" at the foot, the pencil on the lines this person may change.
 */
export function WeeklyScroll({
  lines,
  summary,
  large = false,
  idPrefix = "weekly",
  unroll = false,
  add,
  mayEdit,
  onEdit,
}: {
  lines: readonly ScrollEntry[];
  /** The week's tally; none (a scroll written before 2026-10-06), no tally. */
  summary?: WeeklySummary | null;
  large?: boolean;
  idPrefix?: string;
  unroll?: boolean;
  add?: ScrollAdd;
  mayEdit?: (line: ScrollEntry) => boolean;
  /** The pencil: the line, and the pencil itself, where the focus comes back when the composer closes. */
  onEdit?: (line: ScrollEntry, opener?: HTMLElement) => void;
}) {
  const sections = weeklySections(lines);
  const titleId = `${idPrefix}-title`;
  const { scrollRef, paperRef } = useUnrollFrom(unroll);
  const cls = `weekly-scroll${large ? " weekly-scroll-large" : ""}${unroll ? " weekly-scroll-unroll" : ""}`;
  return (
    <section class={cls} aria-labelledby={titleId} ref={scrollRef}>
      <Roller />
      <div class="weekly-paper" ref={paperRef}>
        <div class="weekly-head">
          <h2 class="weekly-title" id={titleId}>
            {WEEKLY_CARD_TITLE}
          </h2>
          <Divider />
        </div>
        {summary && <Tally summary={summary} />}
        <div class="weekly-writing">
          {/* While the window is open an empty scroll is just its title and "Add yours". */}
          {sections.length === 0 && !add && <p class="weekly-empty">{WEEKLY_EMPTY}</p>}
          {sections.map((s) => (
            <div class="weekly-section" key={s.kind}>
              <h3 class="weekly-heading">{s.heading}</h3>
              <ul class="weekly-lines">
                {s.lines.map((l, i) => (
                  <li class="weekly-line" key={l.id ?? `${l.created_at}-${i}`}>
                    <span class="weekly-line-text">
                      <MentionText text={weeklyLineText(l)} />
                    </span>
                    {onEdit && l.id && mayEdit?.(l) && (
                      <button type="button" class="weekly-edit" aria-label={`Edit: ${l.text}`} onClick={(e) => onEdit(l, e.currentTarget)}>
                        <Pencil />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {add &&
          (add.full ? (
            <p class="weekly-full">{WEEKLY_CAP_REACHED}</p>
          ) : (
            <button type="button" class="btn btn-ghost small-btn weekly-add" ref={add.addRef} aria-expanded={add.expanded} onClick={add.onAdd}>
              {WEEKLY_ADD}
            </button>
          ))}
      </div>
      <Roller />
    </section>
  );
}

/** The scroll's launch sheet: the dim, "Tap to close", the scroll large, Got it. Closing bookmarks the week (the shell's onClose). */
export function WeeklySheet({ scroll, onClose }: { scroll: WeeklyScrollData; onClose: () => void }) {
  const exit = useExit(onClose);
  useScreenCue(exit.closing);
  const close = () => exit.close();
  // Escape (and Android back) closes; focus trapped, the app behind inert (src/focus-trap.ts).
  const sheetRef = useSheetLayer<HTMLDivElement>(!exit.closing, close);
  return (
    <div class={`sheet-backdrop weekly-sheet-backdrop${exit.cls}`} ref={exit.ref} onClick={close}>
      <span class="sheet-tap-close" aria-hidden="true">
        {closeHint()}
      </span>
      <div class="weekly-sheet" ref={sheetRef} role="dialog" aria-modal="true" tabIndex={-1} aria-labelledby="weekly-sheet-title" onClick={(e) => e.stopPropagation()}>
        <WeeklyScroll lines={scrollLines(scroll.data)} summary={scrollSummary(scroll.data)} large unroll idPrefix="weekly-sheet" />
        <button type="button" class="btn btn-primary btn-block" data-autofocus onClick={close}>
          Got it
        </button>
      </div>
    </div>
  );
}
